import React, { FunctionComponent, useEffect } from "react";
import styles from "./Image.module.scss";
import ImageProps from "./ImageProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import isBrowserContext from "helpers/isBrowserContext";

const Image: FunctionComponent<ImageProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, props.settings.imageCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const imageSizeClass = props.settings.imageSize ? styles[props.settings.imageSize] : undefined;

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, imageSizeClass, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={combineClasses([styles.container, imageSizeClass])}>
                <div className={styles.media}>
                    {/* Media */}
                    {props.content.image ? (
                        <ImageViewer src={props.content.image.umbracoFile.src} crop={imageCrop} focalPoint={props.content.image.umbracoFile.focalPoint} alt={props.content.imageAlternative} aspectRatioX={imageAspectRatio.aspectRatioX} aspectRatioY={imageAspectRatio.aspectRatioY} />
                    ) : null}

                    {/* Caption */}
                    {props.content.imageCaption && props.content.imageCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.imageCaption}</div>
                    ) : null}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Image;